import React from 'react';
import PropTypes from 'prop-types';
import { t } from 'i18next';
import {
  getSizeMeasurementAbbreviationString, getWeightMeasurementAbbreviationString, getTotalWeight, capitalizeFirstLetter,
} from '../../../../utils/func';
import {
  CategoryContainer,
  AccessorialsContentBoxInfoRow,
  ContentBox,
  ContentBoxTitle,
  ContentBoxGap,
  ContentBoxSubTitle,
  ContentBoxText,
  ContentBoxInfoRowContainer,
  ContentBoxInfoRow,
  ContentBoxDivider,
  AdditionalMeasurementsContainer,
} from './css';

const Measurements = ({ formData }) => {
  const {
    measurementList,
    measurementType,
    referenceNumber,
    poNumber,
    emergencyPhone,
    dgContactName,
    erapReference,
    erapPhone,
  } = formData;

  const sizeMeasurement = getSizeMeasurementAbbreviationString(measurementType.value);
  const weightMeasurement = getWeightMeasurementAbbreviationString(measurementType.value);

  return (
    <CategoryContainer>
      {measurementList.map((element, index) => (
        <ContentBox key={index}>
          <ContentBoxTitle>
            {t('Item')}
            {' '}
            {index + 1}
          </ContentBoxTitle>
          <ContentBoxInfoRowContainer>
            <ContentBoxInfoRow>
              <ContentBoxSubTitle>{t('Commodity')}</ContentBoxSubTitle>
              <ContentBoxText>
                {element.commodityType && element.commodityType.name
              && capitalizeFirstLetter(element.commodityType.name)}
              </ContentBoxText>
            </ContentBoxInfoRow>
            <ContentBoxInfoRow>
              <ContentBoxSubTitle>{t('Description')}</ContentBoxSubTitle>
              <ContentBoxText>{element.description}</ContentBoxText>
            </ContentBoxInfoRow>
            <ContentBoxInfoRow>
              <ContentBoxSubTitle>{t('NmfcCode')}</ContentBoxSubTitle>
              <ContentBoxText>{element.itemNMFC ? element.itemNMFC : '-'}</ContentBoxText>
            </ContentBoxInfoRow>
          </ContentBoxInfoRowContainer>

          <ContentBoxInfoRowContainer>
            <ContentBoxInfoRow>
              <ContentBoxSubTitle>{t('SkidQty')}</ContentBoxSubTitle>
              <ContentBoxText>{element.SkidQty ? element.SkidQty : 0}</ContentBoxText>
            </ContentBoxInfoRow>
            <ContentBoxInfoRow>
              <ContentBoxSubTitle>{t('PieceQty')}</ContentBoxSubTitle>
              <ContentBoxText>{element.PieceQty ? element.PieceQty : 0}</ContentBoxText>
            </ContentBoxInfoRow>
            <ContentBoxInfoRow>
              <ContentBoxSubTitle>{t('ItemLength')}</ContentBoxSubTitle>
              <ContentBoxText>
                {element.itemLength}
                {' '}
                {sizeMeasurement}
              </ContentBoxText>
            </ContentBoxInfoRow>
            <ContentBoxInfoRow>
              <ContentBoxSubTitle>{t('ItemWidth')}</ContentBoxSubTitle>
              <ContentBoxText>
                {element.itemWidth}
                {' '}
                {sizeMeasurement}
              </ContentBoxText>
            </ContentBoxInfoRow>
            <ContentBoxInfoRow>
              <ContentBoxSubTitle>{t('ItemHeight')}</ContentBoxSubTitle>
              <ContentBoxText>
                {element.itemHeight}
                {' '}
                {sizeMeasurement}
              </ContentBoxText>
            </ContentBoxInfoRow>
            <ContentBoxInfoRow>
              <ContentBoxSubTitle>{t('TotalWeightOfItem')}</ContentBoxSubTitle>
              <ContentBoxText>
                {element.itemWeight}
                {' '}
                {weightMeasurement}
              </ContentBoxText>
            </ContentBoxInfoRow>
          </ContentBoxInfoRowContainer>

          {element.itemDangerous && (
            <>
              <ContentBoxDivider />

              <ContentBoxInfoRowContainer>
                <ContentBoxInfoRow>
                  <ContentBoxSubTitle>{t('DangerousGoods')}</ContentBoxSubTitle>
                  <ContentBoxText>{element.itemDangerous ? t('Yes') : t('No')}</ContentBoxText>
                </ContentBoxInfoRow>
                {/* TODO: LQ display temporarily disabled � not supported by backend (TruckMate) yet.
                {element.itemLimitedQuantity && (
                  <ContentBoxInfoRow>
                    <ContentBoxSubTitle>{t('LimitedQuantity')}</ContentBoxSubTitle>
                    <ContentBoxText>{t('Yes')}</ContentBoxText>
                  </ContentBoxInfoRow>
                )}
                */}
              </ContentBoxInfoRowContainer>
            </>
          )}

          {element.itemDangerous && (
            <ContentBoxInfoRowContainer>
              <ContentBoxInfoRow>
                <ContentBoxSubTitle>{t('ShippingName')}</ContentBoxSubTitle>
                <ContentBoxText>{element.shippingName}</ContentBoxText>
              </ContentBoxInfoRow>
              <ContentBoxInfoRow>
                <ContentBoxSubTitle>{t('UNNumber')}</ContentBoxSubTitle>
                <ContentBoxText>
                  {element.unNumber}
                </ContentBoxText>
              </ContentBoxInfoRow>
              <ContentBoxInfoRow>
                <ContentBoxSubTitle>{t('TotalQuantity')}</ContentBoxSubTitle>
                <ContentBoxText>
                  {element.totalQuantity}
                  {' '}
                  {element.totalQuantityUnits?.name}
                </ContentBoxText>
              </ContentBoxInfoRow>
              <ContentBoxInfoRow>
                <ContentBoxSubTitle>{t('QuantityUnits')}</ContentBoxSubTitle>
                <ContentBoxText>
                  {element.totalQuantityUnits?.name}
                </ContentBoxText>
              </ContentBoxInfoRow>
              <ContentBoxInfoRow>
                <ContentBoxSubTitle>{t('NumberLabelsNeeded')}</ContentBoxSubTitle>
                <ContentBoxText>{element.numberPackRequireLabel}</ContentBoxText>
              </ContentBoxInfoRow>
              <ContentBoxInfoRow>
                <ContentBoxSubTitle>{t('PrimaryClass')}</ContentBoxSubTitle>
                <ContentBoxText>{element.dgPrimaryClass || 'N/A'}</ContentBoxText>
              </ContentBoxInfoRow>
              <ContentBoxInfoRow>
                <ContentBoxSubTitle>{t('SubsidiaryClass')}</ContentBoxSubTitle>
                <ContentBoxText>{element.dgSubsidiaryClass || 'N/A'}</ContentBoxText>
              </ContentBoxInfoRow>
              <ContentBoxInfoRow>
                <ContentBoxSubTitle>{t('PackagingGroup')}</ContentBoxSubTitle>
                <ContentBoxText>{element.dgPackagingGroup || 'N/A'}</ContentBoxText>
              </ContentBoxInfoRow>
              <ContentBoxInfoRow>
                <ContentBoxSubTitle>{t('PackagingType')}</ContentBoxSubTitle>
                <ContentBoxText>{element.dgPackagingType || 'N/A'}</ContentBoxText>
              </ContentBoxInfoRow>
            </ContentBoxInfoRowContainer>
          )}
        </ContentBox>
      ))}

      <AdditionalMeasurementsContainer>
        <ContentBox>
          <ContentBoxGap>
            <AccessorialsContentBoxInfoRow>
              <ContentBoxSubTitle>{t('TotalNumberOfSkidsInShipment')}</ContentBoxSubTitle>
              <ContentBoxText>
                {(measurementList && measurementList.length)
                  ? measurementList.reduce((accum, curr) => (curr.SkidQty
                    ? (Number(accum) + Number(curr.SkidQty))
                    : accum), 0) || 0
                  : '-'}
              </ContentBoxText>
            </AccessorialsContentBoxInfoRow>
          </ContentBoxGap>
        </ContentBox>
        <ContentBox>
          <ContentBoxGap>
            <AccessorialsContentBoxInfoRow>
              <ContentBoxSubTitle>{t('TotalNumberOfPiecesInShipment')}</ContentBoxSubTitle>
              <ContentBoxText>
                {(measurementList && measurementList.length)
                  ? measurementList.reduce((accum, curr) => (curr.PieceQty
                    ? (Number(accum) + Number(curr.PieceQty))
                    : accum),
                  0) || 0
                  : '-'}
              </ContentBoxText>
            </AccessorialsContentBoxInfoRow>
          </ContentBoxGap>
        </ContentBox>

        <ContentBox>
          <ContentBoxGap>
            <AccessorialsContentBoxInfoRow>
              <ContentBoxSubTitle>{t('TotalWeightOfItemsInShipment')}</ContentBoxSubTitle>
              <ContentBoxText>
                {measurementList && getTotalWeight(measurementList)}
                {' '}
                {weightMeasurement}
              </ContentBoxText>
            </AccessorialsContentBoxInfoRow>
          </ContentBoxGap>
        </ContentBox>
      </AdditionalMeasurementsContainer>

      {emergencyPhone && (
        <ContentBox>
          <ContentBoxTitle>{t('Additional Information')}</ContentBoxTitle>
          <ContentBoxInfoRowContainer>
            <ContentBoxInfoRow>
              <ContentBoxSubTitle>{t('Reference')}</ContentBoxSubTitle>
              <ContentBoxText>{referenceNumber}</ContentBoxText>
            </ContentBoxInfoRow>
            <ContentBoxInfoRow>
              <ContentBoxSubTitle>{t('PurchaseOrder')}</ContentBoxSubTitle>
              <ContentBoxText>{poNumber}</ContentBoxText>
            </ContentBoxInfoRow>
            <ContentBoxInfoRow>
              <ContentBoxSubTitle>{t('24hEmergencyNumber')}</ContentBoxSubTitle>
              <ContentBoxText>{emergencyPhone || 'N/A'}</ContentBoxText>
            </ContentBoxInfoRow>
            <ContentBoxInfoRow>
              <ContentBoxSubTitle>{t('DgContactName') || 'Contact Name'}</ContentBoxSubTitle>
              <ContentBoxText>{dgContactName || 'N/A'}</ContentBoxText>
            </ContentBoxInfoRow>
            <ContentBoxInfoRow>
              <ContentBoxSubTitle>{t('ERAPReference')}</ContentBoxSubTitle>
              <ContentBoxText>{erapReference || 'N/A'}</ContentBoxText>
            </ContentBoxInfoRow>
            <ContentBoxInfoRow>
              <ContentBoxSubTitle>{t('ERAPPhone')}</ContentBoxSubTitle>
              <ContentBoxText>{erapPhone || 'N/A'}</ContentBoxText>
            </ContentBoxInfoRow>
          </ContentBoxInfoRowContainer>
        </ContentBox>
      )}
    </CategoryContainer>
  );
};

Measurements.propTypes = {
  formData: PropTypes.shape({
    measurementList: PropTypes.arrayOf(PropTypes.shape({
      commodityType: PropTypes.shape({
        name: PropTypes.string,
      }),
      description: PropTypes.string,
      itemNMFC: PropTypes.string,
      SkidQty: PropTypes.number,
      PieceQty: PropTypes.number,
      itemLength: PropTypes.number,
      itemWidth: PropTypes.number,
      itemHeight: PropTypes.number,
      itemWeight: PropTypes.number,
      itemDangerous: PropTypes.bool,
      shippingName: PropTypes.string,
      unNumber: PropTypes.string,
      totalQuantity: PropTypes.number,
      totalQuantityUnits: PropTypes.shape({
        name: PropTypes.string,
      }),
      numberPackRequireLabel: PropTypes.number,
      dgPrimaryClass: PropTypes.string,
      dgSubsidiaryClass: PropTypes.string,
      dgPackagingGroup: PropTypes.string,
      dgPackagingType: PropTypes.string,
    })),
    measurementType: PropTypes.shape({
      value: PropTypes.string,
    }),
    referenceNumber: PropTypes.string,
    poNumber: PropTypes.string,
    emergencyPhone: PropTypes.string,
    dgContactName: PropTypes.string,
    erapReference: PropTypes.string,
    erapPhone: PropTypes.string,
  }),
};

export default Measurements;
